import React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Slider,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  DuplicateDetectionConfig,
  getDuplicateDetectionConfig,
  updateDuplicateDetectionConfig,
} from '../services/duplicateDetectionConfigService';

type Props = {
  storeId?: string | null;
  storeName?: string | null;
};

const DuplicateDetectionSettings: React.FC<Props> = ({
  storeId,
  storeName,
}) => {
  const [config, setConfig] = React.useState<DuplicateDetectionConfig | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!storeId) {
      setConfig(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const next = await getDuplicateDetectionConfig(storeId);
      setConfig(next);
    } catch (e: any) {
      setError(e?.message || 'Erreur lors du chargement de la détection des doublons.');
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const patch = (partial: Partial<DuplicateDetectionConfig>) => {
    setConfig((prev) => (prev ? { ...prev, ...partial } : prev));
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!storeId || !config) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await updateDuplicateDetectionConfig(
        {
          enabled: !!config.enabled,
          windowMinutes: config.windowMinutes,
          scoreThreshold: config.scoreThreshold,
          distanceMeters: config.distanceMeters,
          ttsEnabled: !!config.ttsEnabled,
        },
        { storeId }
      );
      setConfig(updated);
      setSuccess('Détection doublons enregistrée pour ce magasin.');
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de l’enregistrement des règles de doublons.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Détection doublons
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Règles appliquées au magasin {storeName || storeId || 'courant'}.
          </Typography>
        </Box>

        {!storeId ? (
          <Alert severity="warning">Aucun magasin actif sélectionné.</Alert>
        ) : loading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography>Chargement…</Typography>
          </Stack>
        ) : (
          <>
            {error && <Alert severity="error">{error}</Alert>}
            {success && <Alert severity="success">{success}</Alert>}

            <Alert severity="info">
              Cette configuration s’applique uniquement au magasin actif.
            </Alert>

            <FormControlLabel
              control={
                <Switch
                  checked={config?.enabled ?? true}
                  onChange={(_, checked) => patch({ enabled: checked })}
                />
              }
              label="Activer la détection"
            />

            <Box>
              <Typography gutterBottom>Fenêtre temporelle: {config?.windowMinutes ?? 15} min</Typography>
              <Slider
                value={config?.windowMinutes ?? 15}
                min={2}
                max={60}
                step={1}
                marks
                valueLabelDisplay="auto"
                onChange={(_, value) => patch({ windowMinutes: Number(value) })}
              />
            </Box>

            <Box>
              <Typography gutterBottom>
                Seuil de score: {(config?.scoreThreshold ?? 0.7).toFixed(2)}
              </Typography>
              <Slider
                value={config?.scoreThreshold ?? 0.7}
                min={0.6}
                max={0.9}
                step={0.01}
                valueLabelDisplay="auto"
                onChange={(_, value) => patch({ scoreThreshold: Number(value) })}
              />
            </Box>

            <Box>
              <Typography gutterBottom>Distance GPS: {config?.distanceMeters ?? 120} m</Typography>
              <Slider
                value={config?.distanceMeters ?? 120}
                min={50}
                max={500}
                step={10}
                valueLabelDisplay="auto"
                onChange={(_, value) => patch({ distanceMeters: Number(value) })}
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={config?.ttsEnabled ?? true}
                  onChange={(_, checked) => patch({ ttsEnabled: checked })}
                />
              }
              label="Annonce vocale en cas de risque"
            />

            <Box>
              <Button variant="contained" onClick={handleSave} disabled={saving || !config}>
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
};

export default DuplicateDetectionSettings;
